import { useEffect, useState } from "react";
import { DisaggregatedMetric, DistributionData, GeoDetails, GeoTag, PercentileData } from "../../services/types";
import { fetchDistributionData, fetchPercentileById } from "../../utils/dashboardUtils";
import { categorizeIndexValue } from "../../utils/helpers";

interface TitleBannerProps {
    metric: DisaggregatedMetric | null;
    geoDetails: GeoDetails[];
    geoTag: GeoTag | null;
    svg: string;
    text: string;
}
  
const TitleBanner = ({ metric, svg, text }: TitleBannerProps) => {
    const [countyDistributionData, setCountyDistributionData] = useState<DistributionData[] | null>(null);
    const [percentile, setPercentile] = useState<PercentileData[] | null>(null);
    const [loading, setLoading] = useState(false);

    // Destructure metric properties
    // Provide default values if metric is null
    const id = metric?.id ?? 0;
    const name = metric?.name ?? "Selected Index";
    const versions = metric?.versions ?? [{ value: "-", unit: "-", percent_calc: null }];


    const val = versions[0].unit === "ratio" 
        ? `${versions[0].percent_calc?.n}/${versions[0].percent_calc?.d}` 
        : versions[0].value;

    // Ensure val is a number to pass to categorizeValue
    let numericVal: number;

    if (typeof val === 'string') {
        if (val.includes('/')) {
            // If val is a fraction (e.g., "5/10"), calculate the ratio
            const [numerator, denominator] = val.split('/').map(num => parseFloat(num.trim()));
            numericVal = denominator ? numerator / denominator : 0;
        } else {
            numericVal = parseFloat(val as string) || 0;
        }
    } else {
        numericVal = val;
    }

    // Use the categorizeValue function to get the category text
        const categoryText = percentile 
        ? categorizeIndexValue(numericVal, percentile, false) 
        : loading 
        ? "Loading..." 
        : "-";
    
        // Fetching county distribution and percentile data as soon as the component mounts
        useEffect(() => {
            const fetchData = async () => {
                setLoading(true);
                try {
                    const countyPromise = !countyDistributionData 
                        ? fetchDistributionData(id, [], null) 
                        : Promise.resolve(countyDistributionData);
                        
                    const percentilePromise = !percentile 
                        ? fetchPercentileById(id) 
                        : Promise.resolve(percentile);
        
                    const [countyResult, percentileResult] = await Promise.all([countyPromise, percentilePromise]);
        
                    if (!countyDistributionData) setCountyDistributionData(countyResult || null);
                    if (!percentile) setPercentile(percentileResult?.data || null);
                } catch (error) {
                    console.error('Error fetching data:', error);
                } finally {
                    setLoading(false);
                }
            };
        
            if (id && (!countyDistributionData || !percentile)) {
                fetchData();
            }
        }, [id, countyDistributionData, percentile]);

    return (
    <>
        <div className="border bg-[#1A3A67] rounded-md py-2 flex items-center justify-between px-4">
            <div className="flex items-center">
                <img src={svg} alt={name}/>
                <h2 className="font-bold text-white ml-2">{text}</h2>
            </div>
            
            <h2 className="text-white text-sm">The {name} is <strong>{categoryText}</strong></h2>
        </div>
    </>  
    );
};
  
export default TitleBanner;